import type {Iteration} from '../api/columns/contracts/iteration'
import type {PersistedOption} from '../api/columns/contracts/single-select'
import {compareAscending, compareDescending} from '../helpers/iterations'
import type {ColumnModel} from './column-model'
import type {IterationColumnModel} from './column-model/custom/iteration'
import type {SingleSelectColumnModel} from './column-model/custom/single-select'
import {isAnySingleSelectColumnModel, isIterationColumnModel} from './column-model/guards'
import type {StatusColumnModel} from './column-model/system/status'
import type {Group} from './group'

/** Identifier for reserved "no match" group when rendering board */
export const MissingVerticalGroupId = 'no_vertical_group'

export type VerticalGroup = Group

const verticalGroupsForSingleSelect = (options: Array<PersistedOption>, name: string) => {
  return [
    {
      id: MissingVerticalGroupId,
      name: `No ${name}`,
      nameHtml: `No ${name}`,
    },
    ...options,
  ]
}

const verticalGroupsForIteration = (
  name: string,
  iterations: Array<Iteration>,
  completedIterations: Array<Iteration>,
  {includeAllCompletedIterations = false} = {},
): Array<VerticalGroup> => {
  const sortedCompletedIterations = completedIterations.slice().sort(compareDescending)
  const allIterations = [
    ...(includeAllCompletedIterations ? sortedCompletedIterations : sortedCompletedIterations.slice(0, 3)),
    ...iterations,
  ]
  const iterationsAsGroups = allIterations
    .sort(compareAscending)
    .map(i => ({id: i.id, name: i.title, nameHtml: i.titleHtml}))

  return [
    {
      id: MissingVerticalGroupId,
      name: `No ${name}`,
      nameHtml: `No ${name}`,
    },
    ...iterationsAsGroups,
  ]
}

export function getVerticalGroupsForField(
  columnModel?: ColumnModel,
  opts: {includeAllCompletedIterations?: boolean} = {},
) {
  if (!columnModel) {
    return []
  }

  if (isAnySingleSelectColumnModel(columnModel)) {
    return verticalGroupsForSingleSelect(columnModel.settings.options || [], columnModel.name)
  }

  if (isIterationColumnModel(columnModel)) {
    const {configuration} = columnModel.settings
    const {iterations, completedIterations} = configuration
    return verticalGroupsForIteration(columnModel.name, iterations, completedIterations, opts)
  }

  throw new Error(`Unable to obtain vertical groups for field type: ${columnModel.dataType}`)
}

export function canVerticalGroup(
  columnModel: ColumnModel,
): columnModel is IterationColumnModel | SingleSelectColumnModel | StatusColumnModel {
  return isAnySingleSelectColumnModel(columnModel) || isIterationColumnModel(columnModel)
}
